import { useId, useState } from 'react'
import type { AggStatus } from '../domain/types'
import type { JourneyCalendarLoader } from '../data/DataSource'
import type { ReactNode } from 'react'
import type { JourneyListFilters as Filters } from '../features/journeyListFilters'
import { JourneyDateInput } from './scheduling/JourneyDateInput'
import { Icon } from './Icon'

interface Option { value: string; label: string }

function Choices({ label, options, value, onChange, searchable = true }: {
  searchable?: boolean
  label: string; options: Option[]; value: string[]; onChange: (value: string[]) => void
}) {
  const [search, setSearch] = useState('')
  const normalize = (text: string) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR')
  const matches = options.filter((option) => normalize(option.label).includes(normalize(search.trim())))
  return <fieldset className="fz-listfilters__choices"><legend>{label}</legend>
    {searchable && <input className="fz-listfilters__search" type="search" aria-label={`Buscar ${label.toLocaleLowerCase('pt-BR')}`} placeholder={`Buscar ${label.toLocaleLowerCase('pt-BR')}…`} value={search} onChange={(event) => setSearch(event.target.value)} />}
    <div>{matches.length ? matches.map((option) => <label key={option.value}>
      <input type="checkbox" className="fz-check" checked={value.includes(option.value)} onChange={() => onChange(value.includes(option.value) ? value.filter((item) => item !== option.value) : [...value, option.value])} />
      {option.label}
    </label>) : <span className="fz-listfilters__empty">{options.length ? 'Nenhuma opção encontrada' : 'Nenhuma opção disponível'}</span>}</div>
  </fieldset>
}

export function JourneyListFilters({ quickFilters, value, onChange, onClear, calendarDays, statuses, services, attendants, sessions, dateFilter, onDateFilterChange, loadCalendar }: {
  loadCalendar?: JourneyCalendarLoader
  calendarDays: { date: string; hasCompleteJourney: boolean; journeyCount: number }[]
  quickFilters: ReactNode
  value: Filters; onChange: (value: Filters) => void; onClear: () => void
  statuses: { value: AggStatus; label: string }[]
  services: Option[]; attendants: Option[]; sessions: Option[]
  dateFilter: 'today' | 'all'; onDateFilterChange?: (value: 'today' | 'all') => void
}) {
  const [expanded, setExpanded] = useState(false)
  const changePeriod = (next: Filters) => {
    onDateFilterChange?.(next.from || next.to ? 'all' : 'today')
    onChange(next)
  }
  const id = useId()
  const count = [value.statuses.length > 0, value.services.length > 0, value.attendants.length > 0, value.sessions.length > 0, !!(value.from || value.to) || dateFilter === 'all', value.hideAuto].filter(Boolean).length
  const labels = (options: Option[], selected: string[]) => options.filter((option) => selected.includes(option.value)).map((option) => option.label).join(', ')
  const dateLabel = (value: string) => value.split('-').reverse().join('/')
  const summary = [value.from || value.to ? `${value.from ? dateLabel(value.from) : 'Início'} até ${value.to ? dateLabel(value.to) : 'Sem limite'}` : dateFilter === 'today' ? 'Hoje' : 'Todos os dias',
    labels(statuses, value.statuses), labels(services, value.services), labels(attendants, value.attendants), labels(sessions, value.sessions), value.hideAuto ? 'Sem autoatendimentos' : ''].filter(Boolean).join(' · ')
  return <section className="fz-listfilters" aria-label="Filtros de atendimentos">
    <div className="fz-listfilters__bar">
      {quickFilters}
      <button type="button" className={'fz-btn fz-btn--neutral fz-btn--sm' + (count ? ' is-active' : '')} aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded(!expanded)}>
        <Icon name="search" size={14} /> Filtros{count > 0 && <span className="fz-chip__count">{count}</span>}<Icon name="chevD" size={13} style={expanded ? { transform: 'rotate(180deg)' } : undefined} />
      </button>
      <span className="fz-listfilters__summary" title={summary}>{summary}</span>
      {count > 0 && <button type="button" className="fz-btn fz-btn--ghost fz-btn--sm" onClick={onClear}>Limpar filtros</button>}
    </div>
    {expanded && <div id={id} className="fz-listfilters__panel">
      <div className="fz-listfilters__statuses"><Choices label="Situação da jornada" searchable={false} options={statuses} value={value.statuses} onChange={(statuses) => onChange({ ...value, statuses: statuses as AggStatus[] })} /></div>
      <div className="fz-listfilters__period">
        <span className="fz-field__label">Período das sessões</span>
        {onDateFilterChange && <div className="fz-listfilters__dates">
          <JourneyDateInput label="Data inicial" value={value.from} max={value.to || undefined} days={calendarDays} loadCalendar={loadCalendar} onChange={(from) => changePeriod({ ...value, from })} />
          <JourneyDateInput label="Data final" value={value.to} min={value.from || undefined} days={calendarDays} loadCalendar={loadCalendar} onChange={(to) => changePeriod({ ...value, to })} />
        </div>}
        <label className="fz-toggle"><input type="checkbox" checked={value.hideAuto} onChange={(event) => onChange({ ...value, hideAuto: event.target.checked })} /><span className="fz-toggle__track" />Ocultar autoatendimentos</label>
      </div>
      <Choices label="Serviços" options={services} value={value.services} onChange={(services) => onChange({ ...value, services })} />
      <Choices label="Atendentes" options={attendants} value={value.attendants} onChange={(attendants) => onChange({ ...value, attendants })} />
      <Choices label="Sessões" options={sessions} value={value.sessions} onChange={(sessions) => onChange({ ...value, sessions })} />
    </div>}
  </section>
}
